import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PlatformModal } from './PlatformModal.js';
import type { CoverageOutcome } from '../../api/platform.js';

/**
 * Mark-invoice-paid modal (Phase D-2). Mirrors the server Zod in
 * apps/api/src/routes/platform/invoices.ts (POST .../:id/mark-paid):
 * required payment reference + audit reason, optional paid-at date (defaults to
 * now server-side when omitted). Only an issued/overdue invoice can be marked
 * paid — the server rejects others (409 MUST_ISSUE_FIRST / INVALID_TRANSITION /
 * INVOICE_VOIDED), surfaced via errorMessage.
 */
const schema = z.object({
  paymentReference: z
    .string()
    .trim()
    .min(1, 'Payment reference is required')
    .max(200, 'Payment reference must be 200 characters or fewer'),
  paidAt: z.string().trim().optional(), // 'YYYY-MM-DD' from <input type="date">
  reason: z.string().trim().min(1, 'Reason is required').max(500, 'Reason must be 500 characters or fewer'),
});
type Values = z.infer<typeof schema>;

export function MarkPaidModal({
  open,
  onClose,
  invoiceNumber,
  isSubmitting = false,
  errorMessage,
  onConfirm,
  consequence,
  consequenceError = false,
}: {
  open: boolean;
  onClose: () => void;
  invoiceNumber: string;
  /**
   * ADR-0032 D10 — what marking this invoice paid WILL do to the subscription, from
   * GET …/mark-paid-preview (the same planner mark-paid runs). `undefined` =
   * still loading; `null` = no subscription. Rendered as given, never re-derived.
   */
  consequence?: CoverageOutcome | null;
  consequenceError?: boolean;
  isSubmitting?: boolean;
  errorMessage?: string;
  onConfirm: (body: { reason: string; paymentReference: string; paidAt?: string }) => Promise<void> | void;
}): JSX.Element | null {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { paymentReference: '', paidAt: '', reason: '' } });

  if (!open) return null;

  const handleClose = (): void => {
    reset();
    onClose();
  };

  const submit = (v: Values): void => {
    void onConfirm({
      reason: v.reason,
      paymentReference: v.paymentReference,
      // Date-only → full ISO datetime; omitted means "now" server-side.
      ...(v.paidAt ? { paidAt: new Date(v.paidAt).toISOString() } : {}),
    });
  };

  return (
    <PlatformModal
      open={open}
      onClose={handleClose}
      title="Mark invoice paid"
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="mark-paid-form"
            disabled={isSubmitting}
            className="px-4 py-2 rounded-md font-medium bg-indigo-primary text-white hover:opacity-90 disabled:opacity-50"
          >
            {isSubmitting ? 'Saving…' : 'Mark paid'}
          </button>
        </>
      }
    >
      <form id="mark-paid-form" onSubmit={handleSubmit(submit)}>
        <p className="text-sm text-gray-700 mb-3">
          Record payment for invoice <span className="font-medium">{invoiceNumber}</span>. Status moves to{' '}
          <span className="font-medium">paid</span>.
        </p>
        <Consequence consequence={consequence} error={consequenceError} />

        <label htmlFor="paid-reference" className="block text-sm font-medium text-gray-700 mb-1">
          Payment reference
        </label>
        <input
          id="paid-reference"
          type="text"
          {...register('paymentReference')}
          disabled={isSubmitting}
          placeholder="Wire confirmation / bank reference"
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-primary"
        />
        {errors.paymentReference ? (
          <p className="mt-1 text-sm text-red-600">{errors.paymentReference.message}</p>
        ) : null}

        <label htmlFor="paid-date" className="block text-sm font-medium text-gray-700 mb-1 mt-3">
          Paid on (optional)
        </label>
        <input
          id="paid-date"
          type="date"
          {...register('paidAt')}
          disabled={isSubmitting}
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-primary"
        />
        <p className="mt-1 text-xs text-gray-500">Leave blank to record the payment as of now.</p>

        <label htmlFor="paid-reason" className="block text-sm font-medium text-gray-700 mb-1 mt-3">
          Reason
        </label>
        <textarea
          id="paid-reason"
          {...register('reason')}
          disabled={isSubmitting}
          rows={3}
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-primary"
          placeholder="Written record for the audit trail…"
        />
        {errors.reason ? <p className="mt-1 text-sm text-red-600">{errors.reason.message}</p> : null}
        {errorMessage ? (
          <p className="mt-2 text-sm text-red-600" role="alert">
            {errorMessage}
          </p>
        ) : null}
      </form>
    </PlatformModal>
  );
}

const day = (iso: string | null): string => (iso ? iso.slice(0, 10) : '—');

/** The consequence, stated before the operator confirms. */
function Consequence({ consequence, error }: { consequence?: CoverageOutcome | null; error: boolean }): JSX.Element | null {
  if (error) {
    return (
      <p className="mb-3 rounded-md border border-amber/50 bg-amber/10 px-3 py-2 text-sm text-gray-900" role="status">
        Could not check what this payment will do to the subscription. You can still record it.
      </p>
    );
  }
  if (consequence === undefined) {
    return <p className="mb-3 text-sm text-gray-500">Checking what this payment will do…</p>;
  }
  if (consequence === null) return null;
  const extends_ = consequence.result === 'extended';
  return (
    <div
      className={`mb-3 rounded-md border px-3 py-2 text-sm ${
        // Single-token palette (tailwind.config.js): bare token + opacity, never
        // `amber-50`/`emerald-300` — those compile to nothing and render invisible.
        extends_ ? 'border-emerald/50 bg-emerald/10 text-gray-900' : 'border-amber/50 bg-amber/10 text-gray-900'
      }`}
      role="status"
      aria-label="What this payment will do"
    >
      <p className="font-medium">What this payment will do</p>
      {extends_ ? (
        <p>
          Coverage extends from {day(consequence.fromPeriodEnd)} to <span className="font-medium">{day(consequence.toPeriodEnd)}</span>.
          {consequence.statusChange ? (
            <>
              {' '}
              Status changes from <span className="font-medium">{consequence.statusChange.from.replace('_', ' ')}</span> to{' '}
              <span className="font-medium">{consequence.statusChange.to.replace('_', ' ')}</span>.
            </>
          ) : (
            ' Status does not change.'
          )}
        </p>
      ) : null}
      {consequence.warning ? <p className={extends_ ? 'mt-1' : ''}>{consequence.warning}</p> : null}
    </div>
  );
}
